/**
 * The buyer path's memory (docs/PAYMENTS.md "Buying from the site"; core buyintent.ts). A site buy button opens
 * /app/?signup=1&buy=1&plan=…&state=…&src=…&addon=…; we keep that intent here the moment the app loads, so nothing loses it:
 * not the sign-in (Google's window or the email code), not a reload. It is taken off the address once kept.
 *   - Kept in localStorage (in try/catch; a blocked storage keeps it for this page load) for a day: an old tap never starts
 *     a checkout days later.
 *   - It is only a hint. The plan presets words; the price is always computed by the server from the business.
 *   - Cleared once Stripe's page opens (or the person says Not now), so coming back never loops into Checkout again.
 * The "finish setup" marker remembers that a buyer's business was made by the short buy step, so the rest of the normal setup
 * (name, stage, county, permit, hours, menu) continues after Stripe, whether they added a card or not (never a wall).
 */
import { useCallback, useSyncExternalStore } from "react";
import { BUY_PARAM_NAMES, PLAN_BAND_LABEL, parseBuyIntent, type BuyIntent, type UsState } from "@logwave/core";

export const BUY_KEY = "cc.buy.intent";
export const FINISH_KEY = "cc.buy.finish";
/** How long a buy tap is remembered: one day. */
export const BUY_TTL_MS = 24 * 3_600_000;

export interface PendingBuy extends BuyIntent { at: string }
export interface FinishSetup { orgId: string; locationId: string; typeId?: string; state?: UsState; name?: string }

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
let memory: PendingBuy | null = null;
let finishMemory: FinishSetup | null = null;

/** A visit from a site buy button: keep it. Returns it, or null for a link that isn't one. */
export function captureBuy(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): PendingBuy | null {
  const i = parseBuyIntent(search);
  if (!i) return null;
  const v: PendingBuy = { ...i, at: now.toISOString() };
  memory = v;
  try { storage?.setItem(BUY_KEY, JSON.stringify(v)); } catch { /* private mode: this page load only */ }
  notify();
  return v;
}

/** The waiting intent, re-checked on every read (an unknown value stored by hand is dropped), or null when none or too old. */
export function pendingBuy(storage: Storage | undefined = safeStorage(), now = new Date()): PendingBuy | null {
  // Storage first; the in-memory copy only where storage can't be used (private browsing with site data blocked).
  let v: PendingBuy | null;
  try { v = storage ? JSON.parse(storage.getItem(BUY_KEY) ?? "null") as PendingBuy | null : memory; } catch { v = memory; }
  if (!v || typeof v.at !== "string") return null;
  const at = Date.parse(v.at);
  if (!Number.isFinite(at) || now.getTime() - at > BUY_TTL_MS || at - now.getTime() > 60_000) return null;
  const q = new URLSearchParams({ buy: "1", ...(v.plan ? { plan: v.plan } : {}), ...(v.state ? { state: v.state } : {}), ...(v.src ? { src: v.src } : {}), ...(v.addon ? { addon: v.addon } : {}) });
  const clean = parseBuyIntent(q);
  return clean ? { ...clean, at: v.at } : null;
}

export function clearBuy(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(BUY_KEY); } catch { /* ignore */ }
  notify();
}

/** The address without the buy parameters (signup=1 stays: it still titles the sign-in "Start your 25 days free"). */
export function withoutBuyParams(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of BUY_PARAM_NAMES) p.delete(k);
  const s = p.toString();
  return s ? `?${s}` : "";
}

const snapshot = (): string => { try { return `${localStorage.getItem(BUY_KEY) ?? JSON.stringify(memory)}|${localStorage.getItem(FINISH_KEY) ?? JSON.stringify(finishMemory)}`; } catch { return `${JSON.stringify(memory)}|${JSON.stringify(finishMemory)}`; } };
/** The waiting intent, live. */
export function useBuyIntent(): { pending: PendingBuy | null; clear: () => void } {
  useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, snapshot, () => "null");
  const clear = useCallback(() => clearBuy(), []);
  return { pending: pendingBuy(), clear };
}

/** The words for the band the visitor tapped on the site (never a price promise: the price is the server's). */
export function pickedLine(plan: BuyIntent["plan"]): string | null {
  if (!plan) return null;
  if (plan === "building") return "You picked the church and community kitchen plan.";
  return `You picked ${PLAN_BAND_LABEL[plan]}.`;
}

/* ─────────────── the rest of setup, after Stripe ─────────────── */

export function markFinishSetup(f: FinishSetup, storage: Storage | undefined = safeStorage()) {
  finishMemory = f;
  try { storage?.setItem(FINISH_KEY, JSON.stringify(f)); } catch { /* this page load only */ }
  notify();
}
export function finishSetupFor(orgId: string | undefined, storage: Storage | undefined = safeStorage()): FinishSetup | null {
  if (!orgId) return null;
  let v: FinishSetup | null;
  try { v = storage ? JSON.parse(storage.getItem(FINISH_KEY) ?? "null") as FinishSetup | null : finishMemory; } catch { v = finishMemory; }
  return v && v.orgId === orgId && typeof v.locationId === "string" ? v : null;
}
export function clearFinishSetup(storage: Storage | undefined = safeStorage()) {
  finishMemory = null;
  try { storage?.removeItem(FINISH_KEY); } catch { /* ignore */ }
  notify();
}
export function useFinishSetup(orgId: string | undefined): FinishSetup | null {
  useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, snapshot, () => "null");
  return finishSetupFor(orgId);
}
